/**
 * Pricing configuration for all game packages
 * Prices are in EUR (€)
 */

export enum PackageType {
  BLITZ_WEEKEND = 'blitz_weekend',
  BLITZ_WEEK = 'blitz_week',
  BLITZ_MONTH = 'blitz_month',
  TEAM_WEEKEND = 'team_weekend',
  TEAM_WEEK = 'team_week',
  TEAM_MONTH = 'team_month',
  FAMILY_WEEKEND = 'family_weekend',
  FAMILY_WEEK = 'family_week',
  FAMILY_MONTH = 'family_month',
  PARTY_WEEKEND = 'party_weekend',
  PARTY_WEEK = 'party_week',
  PARTY_MONTH = 'party_month',
}

export interface PricingPackage {
  id: PackageType;
  name: string;
  description: string;
  price: number; // in EUR
  currency: string;
  duration: string; // e.g., "2 days", "7 days", "30 days"
  gameType: 'blitz' | 'team' | 'family' | 'party';
}

function pack(
  id: PackageType,
  gameLabel: string,
  gameType: PricingPackage['gameType'],
  tier: 'weekend' | 'week' | 'month',
  price: number,
  duration: string,
  daysLabel: string
): PricingPackage {
  const tierNames = {
    weekend: 'Weekend Package',
    week: 'Week Package',
    month: 'Monthly Package',
  };
  return {
    id,
    name: tierNames[tier],
    description: `${daysLabel} access to all ${gameLabel} games`,
    price,
    currency: 'EUR',
    duration,
    gameType,
  };
}

export const PRICING_PACKAGES: Record<PackageType, PricingPackage> = {
  [PackageType.BLITZ_WEEKEND]: pack(PackageType.BLITZ_WEEKEND, 'Blitz', 'blitz', 'weekend', 2.99, '2 days', '2 days'),
  [PackageType.BLITZ_WEEK]: pack(PackageType.BLITZ_WEEK, 'Blitz', 'blitz', 'week', 4.99, '7 days', '7 days'),
  [PackageType.BLITZ_MONTH]: pack(PackageType.BLITZ_MONTH, 'Blitz', 'blitz', 'month', 9.99, '30 days', '30 days'),
  [PackageType.TEAM_WEEKEND]: pack(PackageType.TEAM_WEEKEND, 'Team', 'team', 'weekend', 2.99, '2 days', '2 days'),
  [PackageType.TEAM_WEEK]: pack(PackageType.TEAM_WEEK, 'Team', 'team', 'week', 4.99, '7 days', '7 days'),
  [PackageType.TEAM_MONTH]: pack(PackageType.TEAM_MONTH, 'Team', 'team', 'month', 9.99, '30 days', '30 days'),
  [PackageType.FAMILY_WEEKEND]: pack(PackageType.FAMILY_WEEKEND, 'Family', 'family', 'weekend', 2.99, '2 days', '2 days'),
  [PackageType.FAMILY_WEEK]: pack(PackageType.FAMILY_WEEK, 'Family', 'family', 'week', 4.99, '7 days', '7 days'),
  [PackageType.FAMILY_MONTH]: pack(PackageType.FAMILY_MONTH, 'Family', 'family', 'month', 9.99, '30 days', '30 days'),
  [PackageType.PARTY_WEEKEND]: pack(PackageType.PARTY_WEEKEND, 'Party', 'party', 'weekend', 2.99, '2 days', '2 days'),
  [PackageType.PARTY_WEEK]: pack(PackageType.PARTY_WEEK, 'Party', 'party', 'week', 4.99, '7 days', '7 days'),
  [PackageType.PARTY_MONTH]: pack(PackageType.PARTY_MONTH, 'Party', 'party', 'month', 9.99, '30 days', '30 days'),
};

/**
 * Get pricing packages by game type
 */
export function getPricingPackagesByGameType(gameType: 'blitz' | 'team' | 'family' | 'party'): PricingPackage[] {
  return Object.values(PRICING_PACKAGES).filter(pkg => pkg.gameType === gameType);
}

/**
 * Get all pricing packages
 */
export function getAllPricingPackages(): PricingPackage[] {
  return Object.values(PRICING_PACKAGES);
}
